import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Edit, CheckCircle, Eye } from 'lucide-react';
import { getMyProducts, deleteProduct, markProductSold, DbProduct } from '../../utils/api';
import { useAuth } from '../../components/auth/AuthContext';

const MyProducts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [products, setProducts] = useState<DbProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    const loadProducts = async () => {
      try {
        const response = await getMyProducts();
        if (response.success) setProducts(response.data);
        else setError(response.message);
      } catch { setError('Fehler beim Laden deiner Produkte'); }
      finally { setLoading(false); }
    };
    loadProducts();
  }, [user, navigate]);

  const handleDelete = async (e: React.MouseEvent, productId: number) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Möchtest du dieses Produkt wirklich löschen?')) return;
    const response = await deleteProduct(productId);
    if (response.success) setProducts(products.filter(p => p.id !== productId));
    else setError(response.message || 'Fehler beim Löschen');
  };

  const handleMarkSold = async (e: React.MouseEvent, productId: number) => {
    e.preventDefault(); e.stopPropagation();
    if (!window.confirm('Produkt als verkauft markieren? Es wird dann nicht mehr öffentlich angezeigt.')) return;
    const response = await markProductSold(productId);
    if (response.success) {
      setProducts(products.map(p => p.id === productId ? { ...p, is_archived: true } : p));
    } else setError(response.message || 'Fehler');
  };

  if (loading) return <div className="text-center py-8 text-gray-600">Lade Produkte...</div>;

  const active = products.filter(p => !p.is_archived);
  const sold = products.filter(p => p.is_archived);

  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Meine Produkte</h1>
        <button
          onClick={() => navigate('/products/create')}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors flex items-center gap-2"
        >
          <Plus size={20} />
          <span className="hidden sm:inline">Produkt erstellen</span>
          <span className="sm:hidden">Neu</span>
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">{error}</div>
      )}

      {products.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <p className="text-gray-600 mb-4">Du hast noch keine Produkte eingestellt</p>
          <button onClick={() => navigate('/products/create')} className="bg-orange-500 text-white px-6 py-2 rounded-lg hover:bg-orange-600 transition-colors inline-flex items-center gap-2">
            <Plus size={20} /> Erstes Produkt erstellen
          </button>
        </div>
      ) : (
        <>
          {/* Aktive Produkte */}
          {active.length > 0 && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-700 mb-3">Aktive Angebote ({active.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {active.map(product => (
                  <div key={product.id} className="bg-white rounded-lg shadow-md overflow-hidden cursor-pointer" onClick={() => navigate(`/products/${product.id}`)}>
                    <div className="aspect-square relative">
                      <img
                        src={product.image_url ? `${window.location.origin}${product.image_url}` : '/placeholder-image.jpg'}
                        alt={product.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-800 text-lg mb-1 truncate">{product.title}</h3>
                      <p className="text-gray-500 text-sm mb-1">{product.club || 'Vereinslos'}</p>
                      <p className="text-xl font-bold text-orange-500 mb-1">{Number(product.price).toFixed(2)} €</p>
                      {product.views !== undefined && (
                        <p className="text-gray-400 text-xs flex items-center gap-1 mb-3">
                          <Eye size={12} /> {product.views} {product.views === 1 ? 'Aufruf' : 'Aufrufe'}
                        </p>
                      )}
                      <div className="flex gap-2" onClick={e => e.stopPropagation()}>
                        <button onClick={e => { e.stopPropagation(); navigate(`/products/edit/${product.id}`); }} className="flex-1 bg-blue-100 text-blue-700 px-3 py-2 rounded hover:bg-blue-200 transition-colors flex items-center justify-center gap-1 text-sm">
                          <Edit size={14} /> Bearbeiten
                        </button>
                        <button onClick={e => handleMarkSold(e, product.id)} className="flex-1 bg-green-100 text-green-700 px-3 py-2 rounded hover:bg-green-200 transition-colors flex items-center justify-center gap-1 text-sm">
                          <CheckCircle size={14} /> Verkauft
                        </button>
                        <button onClick={e => handleDelete(e, product.id)} className="bg-red-100 text-red-700 px-3 py-2 rounded hover:bg-red-200 transition-colors">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Verkaufte Produkte */}
          {sold.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-gray-500 mb-3">Verkauft ({sold.length})</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                {sold.map(product => (
                  <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden opacity-60">
                    <div className="aspect-square relative">
                      <img
                        src={product.image_url ? `${window.location.origin}${product.image_url}` : '/placeholder-image.jpg'}
                        alt={product.title}
                        className="w-full h-full object-cover grayscale"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="bg-green-600 text-white font-bold px-4 py-2 rounded-full text-sm rotate-[-15deg] shadow-lg">VERKAUFT</span>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-600 text-lg mb-1 truncate">{product.title}</h3>
                      <p className="text-gray-400 text-sm">{Number(product.price).toFixed(2)} €</p>
                      <div className="mt-3" onClick={e => e.stopPropagation()}>
                        <button onClick={e => handleDelete(e, product.id)} className="w-full bg-red-100 text-red-700 px-3 py-2 rounded hover:bg-red-200 transition-colors flex items-center justify-center gap-1 text-sm">
                          <Trash2 size={14} /> Endgültig löschen
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MyProducts;
